import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import NoPermissionText from '@/components/atoms/NoPermissionText';
import { RequirementControlTemplateButton } from '@/components/atoms/RequirementControlTemplateButton';
import Spinner from '@/components/atoms/Spinner';
import NewSideFilter from '@/components/molecules/NewSideFilter';
import { RequirementControlDownloadButton } from '@/components/molecules/RequirementControlDownloadButton';
import { RequirementControlHistoryDownloadButton } from '@/components/molecules/RequirementControlHistoryDownloadButton';
import { RequirementControlUploadButton } from '@/components/molecules/RequirementControlUploadButton';
import RequirementControlCard from '@/components/organisms/RequirementControlCard';
import { filters } from '@/constants/filters/RequirementControlFilter';
import { requirementControlAtom } from '@/state/RequirementControl.atom';
import {
	EmployeeWithRequirementControl,
	Indicators,
} from '@/types/RequirementControl';
import {
	userCanEditRequirementControl,
	userCanSeeRequirementControl,
} from '@/utils/handleSavePermissions';
import EmptyIcon from '../../assets/icons/empty.svg';
import Cards from '../../components/molecules/Cards';
import ExpandableSubtitle from '../../components/molecules/ExpandableSubtitle';
import ViewDataWrapper from '../../components/molecules/ViewDataWrapper';
import api from '../../services/apiSgft';

const RequirementControlPage = () => {
	const filterValues = useRecoilValue(requirementControlAtom);
	const { ref, inView } = useInView();

	const fetchControl = async ({
		pageParam = 1,
	}): Promise<EmployeeWithRequirementControl[]> => {
		try {
			const response = await api.get(`requirement-control`, {
				params: {
					size: 20,
					page: pageParam,
					employee: filterValues.employee,
					generalInformationJobTitle: filterValues.generalInformationJobTitle,
					management: filterValues.management,
					coordination: filterValues.coordination,
					pole: filterValues.pole,
					workStation: filterValues.workStation,
					status: filterValues.status,
					situation: filterValues.situation,
					requirementType: filterValues.requirementType,
					requirementClass: filterValues.requirementClass,
					requirement: filterValues.requirement,
				},
			});
			return response.data;
		} catch (e) {
			toast.error('Erro ao carregar os dados', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		}
	};

	const fetchIndicators = async (): Promise<Indicators> => {
		try {
			const response = await api.get('requirement-control/indicators', {
				params: {
					employee: filterValues.employee,
					generalInformationJobTitle: filterValues.generalInformationJobTitle,
					management: filterValues.management,
					coordination: filterValues.coordination,
					pole: filterValues.pole,
					workStation: filterValues.workStation,
					status: filterValues.status,
					situation: filterValues.situation,
					requirementType: filterValues.requirementType,
					requirementClass: filterValues.requirementClass,
					requirement: filterValues.requirement,
				},
			});
			return response.data;
		} catch (e) {
			toast.error('Erro ao carregar indicadores', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		}
	};

	const {
		data,
		isLoading,
		isError,
		fetchNextPage,
		isFetchingNextPage,
		hasNextPage,
	} = useInfiniteQuery(['requirement-control', filterValues], fetchControl, {
		retry: false,
		getNextPageParam: (lastPage, allPages) => {
			return lastPage.length > 0 ? allPages.length + 1 : undefined;
		},
	});

	const {
		data: indicators,
		isLoading: isLoadingIndicators,
		isError: isErrorIndicators,
	} = useQuery(
		['requirement-control-indicators', filterValues],
		fetchIndicators,
		{
			retry: false,
		},
	);
	useEffect(() => {
		if (inView && hasNextPage) {
			fetchNextPage();
		}
	}, [fetchNextPage, inView, hasNextPage]);
	const fetchFilterOptions = async () => {
		const response = await api.get('requirement-control/filter-options', {
			params: {
				employee: filterValues.employee,
				generalInformationJobTitle: filterValues.generalInformationJobTitle,
				management: filterValues.management,
				coordination: filterValues.coordination,
				pole: filterValues.pole,
				workStation: filterValues.workStation,
				status: filterValues.status,
				situation: filterValues.situation,
				requirementType: filterValues.requirementType,
				requirementClass: filterValues.requirementClass,
				requirement: filterValues.requirement,
			},
		});
		const data = response.data;
		const converted: Record<string, { label: string; value: string }[]> = {};
		for (const key in data) {
			if (Array.isArray(data[key])) {
				converted[key] = data[key].map((value: string) => ({
					label: value,
					value: value,
				}));
			} else {
				converted[key] = [];
			}
		}
		return converted;
	};

	const { data: filterOptions, isLoading: isLoadingFiltersOptions } = useQuery(
		['requirement-control-filters', filterValues],
		fetchFilterOptions,
	);

	const requirementsControl = data?.pages.flat();

	if (!userCanSeeRequirementControl()) return <NoPermissionText />;
	return (
		<div className="relative flex max-h-screen w-full flex-col items-start">
			<div className="absolute -top-10 right-20 flex items-center">
				{userCanEditRequirementControl() && (
					<>
						<RequirementControlTemplateButton />
						<RequirementControlUploadButton filteredData={filterValues} />
					</>
				)}
				<RequirementControlDownloadButton filteredData={filterValues} />
				<RequirementControlHistoryDownloadButton filteredData={filterValues} />
			</div>
			<NewSideFilter
				filters={filters}
				atom={requirementControlAtom}
				options={filterOptions}
				isLoading={isLoadingFiltersOptions}
			/>
			<ViewDataWrapper
				isError={isError || isErrorIndicators}
				isLoading={isLoading || isLoadingIndicators}
				className="w-full"
			>
				<div className="w-full">
					{indicators?.requirementPendencies && (
						<ExpandableSubtitle
							subtitle={'Pendências por Requisito'}
							hasTooltip
						>
							{indicators?.requirementPendencies.length > 0 ? (
								<Cards
									cards={indicators?.requirementPendencies.map((el) => ({
										value: el.count,
										status: 'grey',
										title: el.name,
									}))}
								/>
							) : (
								<div className="p-2 font-graphie text-[15px] text-[#4A4A4A]">
									Não existem requisitos com pendências
								</div>
							)}
						</ExpandableSubtitle>
					)}
					{indicators?.statusCounts && (
						<ExpandableSubtitle subtitle={'Requisito por Status'}>
							<Cards
								cards={[
									{
										value: indicators.statusCounts.Vencidos,
										status: 'red',
										title: 'Vencidos',
									},
									{
										value: indicators.statusCounts.VenceEm1Mes,
										status: '#dbdb00',
										title: 'Vence em 1 mês',
									},
									{
										value: indicators.statusCounts.VenceEm3Meses,
										status: '#dbdb00',
										title: 'Vence em 3 meses',
									},
									{
										value: indicators.statusCounts.Validos,
										status: 'green',
										title: 'Válidos',
									},
								]}
							/>
						</ExpandableSubtitle>
					)}
				</div>
				<div className="overflow-auto">
					{requirementsControl?.length ? (
						<div className="custom-scrollbar flex h-full flex-col gap-2 overflow-y-scroll rounded-xl pb-4 pr-3">
							{requirementsControl?.map(
								(
									employeeWithRequirementControls: EmployeeWithRequirementControl,
								) => {
									return (
										<RequirementControlCard
											key={employeeWithRequirementControls.employee.id}
											employeeWithRequirementControls={
												employeeWithRequirementControls
											}
										/>
									);
								},
							)}
							{hasNextPage && (
								<div
									className="flex w-full items-center justify-center"
									ref={ref}
								>
									{isFetchingNextPage && <Spinner size={30} />}
								</div>
							)}
						</div>
					) : (
						<div className="flex h-full w-full flex-col items-center justify-center rounded-md bg-white">
							<img
								src={EmptyIcon}
								className="w-40"
								alt="Ícone de lista vazia"
							/>
							<div className="w-full text-center text-sm">
								<p>Nenhum requerimento foi encontrado com esses parâmetros</p>
								<span className="font-semibold text-blue-800">
									Cadastre um ou altere os valores dos filtros para visualizar a
									tabela!
								</span>
							</div>
						</div>
					)}
				</div>
			</ViewDataWrapper>
		</div>
	);
};

export default RequirementControlPage;
